import { useLanguage } from '../i18n/LanguageContext';
import React, { useState, useEffect, useRef } from 'react';
import { NavLink, Outlet } from "react-router-dom";
import "./Layout.css";
import logoImage from "../assets/logo.png";
import { getCurrentUser } from "../utils/auth";

const createIcon = (paths) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {paths}
  </svg>
);

const menuItems = [
  { path: "/users", label: "사용자관리", icon: createIcon(
      <>
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
        <circle cx="12" cy="7" r="4"></circle>
      </>
    ) },
  { path: "/devices", label: "기기관리", icon: createIcon(
      <>
        <rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect>
        <line x1="12" y1="18" x2="12.01" y2="18"></line>
      </>
    ) },
  { path: "/coupons", label: "쿠폰관리", icon: createIcon(
      <>
        <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2z"></path>
        <line x1="12" y1="8" x2="12" y2="16"></line>
      </>
    ) },
  { path: "/credits", label: "크레딧관리", icon: createIcon(
      <>
        <line x1="12" y1="1" x2="12" y2="23"></line>
        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
      </>
    ) },
  { path: "/mails", label: "메일관리", icon: createIcon(
      <>
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
        <polyline points="22,6 12,13 2,6"></polyline>
      </>
    ) },
  { path: "/login-events", label: "로그인이벤트", icon: createIcon(
      <>
        <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path>
        <polyline points="10 17 15 12 10 7"></polyline>
        <line x1="15" y1="12" x2="3" y2="12"></line>
      </>
    ) },
  { path: "/settings", label: "설정", icon: createIcon(
      <>
        <circle cx="12" cy="12" r="3"></circle>
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 8.92 4a1.65 1.65 0 0 0 1-1.51V2a2 2 0 1 1 4 0v.09A1.65 1.65 0 0 0 15 3.6a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9c.14.48.51.86 1 1h.09a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1 1z"></path>
      </>
    ) },
];

const languageOptions = [
  { code: 'ko', label: '한국어', flag: '🇰🇷' },
  { code: 'en', label: 'English', flag: '🇺🇸' },
  { code: 'ja', label: '日本語', flag: '🇯🇵' },
];

function Layout({ onLogout }) {
  const { t, language, changeLanguage } = useLanguage();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const selectedLanguage = languageOptions.find(option => option.code === language);
  const profileRef = useRef(null);
  const languageRef = useRef(null);
  const currentUser = getCurrentUser();
  const adminInfo = {
    name: currentUser?.username || "관리자",
    username: currentUser?.username || "-",
    email: currentUser?.email || "-",
    role: currentUser?.role || "Admin",
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
      if (languageRef.current && !languageRef.current.contains(event.target)) {
        setIsLanguageOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="layout">
      <aside className="sidebar">
        <div className="sidebar-header">
          <img src={logoImage} alt="INVANT Logo" className="sidebar-brand-logo" />
          <p className="sidebar-brand-subtitle">{t("고객지원 통합 관리 시스템")}</p>
        </div>
        <nav className="sidebar-nav">
          {menuItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `sidebar-link ${isActive ? "active" : ""}`
              }
            >
              <span className="sidebar-icon">{item.icon}</span>
              <span className="sidebar-label">{t(item.label)}</span>
            </NavLink>
          ))}
        </nav>
        <div className="sidebar-footer">
          <span className="sidebar-footer-text">{t("v0.1.0 · 검토용")}</span>
        </div>
      </aside>

      <div className="main-wrapper">
        <header className="topbar">
          <div className="topbar-spacer"></div>
          
          <div className="topbar-actions">
            <div className="profile-container" ref={profileRef}>
              <button
                className="profile-btn"
                type="button"
                aria-expanded={isProfileOpen}
                onClick={() => {
                  setIsProfileOpen(!isProfileOpen);
                  setIsLanguageOpen(false);
                }}
              >
                <span className="profile-avatar">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                </span>
                <div className="profile-name-group">
                  <span className="profile-role-badge">{adminInfo.role}</span>
                  <span className="profile-user-name">{adminInfo.name}</span>
                </div>
              </button>

              {isProfileOpen && (
                <div className="profile-dropdown">
                  <div className="profile-info">
                    <div className="profile-info-name">{adminInfo.name}</div>
                    <div className="profile-info-detail profile-info-strong">{adminInfo.email}</div>
                    <div className="profile-info-role">{adminInfo.role}</div>
                  </div>
                  <div className="profile-dropdown-divider"></div>
                  <button className="dropdown-logout-btn" onClick={onLogout}>
                    {t("로그아웃")}
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                      <polyline points="16 17 21 12 16 7"></polyline>
                      <line x1="21" y1="12" x2="9" y2="12"></line>
                    </svg>
                  </button>
                </div>
              )}
            </div>

            <div className="language-container" ref={languageRef}>
              <button
                className="language-btn"
                type="button"
                aria-label={t("언어 선택")}
                aria-expanded={isLanguageOpen}
                onClick={() => {
                  setIsLanguageOpen(!isLanguageOpen);
                  setIsProfileOpen(false);
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="2" y1="12" x2="22" y2="12"></line>
                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
                </svg>
                <span className="language-flag" aria-hidden="true">{selectedLanguage.flag}</span>
                <span>{selectedLanguage.label}</span>
                <svg className={`language-chevron ${isLanguageOpen ? 'open' : ''}`} xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>

              {isLanguageOpen && (
                <div className="language-dropdown">
                  {languageOptions.map((language) => (
                    <button
                      key={language.code}
                      type="button"
                      disabled={language.code === 'ja'}
                      title={language.code === 'ja' ? t('일본어는 준비 중입니다.') : undefined}
                      className={`language-option ${selectedLanguage.code === language.code ? 'active' : ''}`}
                      onClick={() => {
                        changeLanguage(language.code);
                        setIsLanguageOpen(false);
                      }}
                    >
                      <span className="language-option-label">
                        <span className="language-flag" aria-hidden="true">{language.flag}</span>
                        <span>{language.label}</span>
                      </span>
                      {selectedLanguage.code === language.code && (
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="main-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export default Layout;
